import { FormEvent } from "react";
import {
    Box,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    Typography,
    useTheme,
} from "@mui/material";
import { Button, FoodsIconButton, FoodsTextInput } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";

import { TransformationReferenceAdmin } from "src/api/sure-api/useTransformationReferencesAdmin";

export type TransformationReferenceFormState = {
    code: string;
    name: string;
    ecobalyseProcessUuid: string;
    ssq: string;
    sourceVersion: string;
};

type TransformationReferenceDialogProps = {
    open: boolean;
    form: TransformationReferenceFormState;
    isSaving: boolean;
    isEditing: boolean;
    onClose: () => void;
    onChange: (form: TransformationReferenceFormState) => void;
    onSubmit: () => void;
};

export const TransformationReferenceDialog = (props: TransformationReferenceDialogProps) => {
    const { open, form, isSaving, isEditing, onClose, onChange, onSubmit } = props;
    const { t } = useTranslation();
    const theme = useTheme();

    const updateField = (field: keyof TransformationReferenceFormState) => (value: string) => {
        onChange({ ...form, [field]: value });
    };

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        onSubmit();
    };

    const title =
        isEditing ?
            t("transformation.references.edit_title", "Edit transformation process")
        :   t("transformation.references.create_title", "Create transformation process");

    return (
        <Dialog
            open={open}
            onClose={isSaving ? undefined : onClose}
            fullWidth
            maxWidth="sm"
            aria-labelledby="transformation-reference-dialog-title"
            PaperProps={{
                sx: {
                    backgroundColor: theme.custom.grey[100],
                    borderRadius: theme.spacing(1.5),
                },
            }}
        >
            <Box component="form" onSubmit={handleSubmit}>
                <DialogTitle
                    id="transformation-reference-dialog-title"
                    sx={{
                        borderBottom: `${theme.custom.strokes.containerDivider.width} ${theme.custom.strokes.containerDivider.style} ${theme.custom.strokes.containerDivider.color}`,
                        padding: theme.spacing(3, 4),
                    }}
                >
                    <Stack direction="row" alignItems="center" justifyContent="space-between" gap={theme.spacing(2)}>
                        <Typography component="span" variant="h2">
                            {title}
                        </Typography>
                        <FoodsIconButton
                            size="m"
                            icon="close"
                            buttonProps={{
                                "aria-label": t("common.close", "Close"),
                                disabled: isSaving,
                                onClick: onClose,
                                type: "button",
                            }}
                        />
                    </Stack>
                </DialogTitle>

                <DialogContent
                    sx={{
                        padding: theme.spacing(4),
                        "&&": { paddingTop: theme.spacing(4) },
                    }}
                >
                    <Stack gap={theme.spacing(3)}>
                        <TransformationReferenceField
                            id="transformation-reference-code"
                            label={t("common.code", "Code")}
                            value={form.code}
                            onChange={updateField("code")}
                            required
                            disabled={isSaving}
                        />
                        <TransformationReferenceField
                            id="transformation-reference-name"
                            label={t("common.name", "Name")}
                            value={form.name}
                            onChange={updateField("name")}
                            required
                            disabled={isSaving}
                        />
                        <TransformationReferenceField
                            id="transformation-reference-ecobalyse"
                            label={t("transformation.references.ecobalyse_uuid", "Ecobalyse process UUID")}
                            value={form.ecobalyseProcessUuid}
                            onChange={updateField("ecobalyseProcessUuid")}
                            disabled={isSaving}
                        />
                        <TransformationReferenceField
                            id="transformation-reference-ssq"
                            label={t("transformation.references.ssq_iri", "SSQ IRI")}
                            value={form.ssq}
                            onChange={updateField("ssq")}
                            placeholder="/api/ssqs/1"
                            disabled={isSaving}
                        />
                        <TransformationReferenceField
                            id="transformation-reference-source-version"
                            label={t("transformation.references.source_version_iri", "Source version IRI")}
                            value={form.sourceVersion}
                            onChange={updateField("sourceVersion")}
                            placeholder="/api/source_versions/1"
                            disabled={isSaving}
                        />
                    </Stack>
                </DialogContent>

                <DialogActions
                    sx={{
                        borderTop: `${theme.custom.strokes.containerDivider.width} ${theme.custom.strokes.containerDivider.style} ${theme.custom.strokes.containerDivider.color}`,
                        gap: theme.spacing(2),
                        justifyContent: "space-between",
                        padding: theme.spacing(3, 4),
                    }}
                >
                    <Button variant="ternary" onClick={onClose} disabled={isSaving}>
                        {t("primarydialog.cancel", "Cancel")}
                    </Button>
                    <Button type="submit" variant="primary" disabled={isSaving} aria-label={t("common.save", "Save")}>
                        {isSaving ?
                            <CircularProgress size={theme.spacing(2.5)} color="inherit" />
                        :   t("common.save", "Save")}
                    </Button>
                </DialogActions>
            </Box>
        </Dialog>
    );
};

type TransformationReferenceFieldProps = {
    id: string;
    label: string;
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    required?: boolean;
    disabled?: boolean;
};

const TransformationReferenceField = (props: TransformationReferenceFieldProps) => {
    const { id, label, value, onChange, placeholder, required = false, disabled = false } = props;
    const theme = useTheme();

    return (
        <Stack gap={theme.spacing(1)}>
            <Typography component="label" htmlFor={id} variant="body-semibold">
                {label}
                {required ? "*" : ""}
            </Typography>
            <FoodsTextInput
                value={value}
                onChange={onChange}
                disabled={disabled}
                ExtraInputProps={{
                    inputProps: {
                        "aria-label": label,
                        id,
                        placeholder,
                        required,
                        "aria-required": required,
                    },
                }}
            />
        </Stack>
    );
};

type TransformationReferenceDeleteDialogProps = {
    reference: TransformationReferenceAdmin | null;
    isDeleting: boolean;
    onClose: () => void;
    onConfirm: () => void;
};

export const TransformationReferenceDeleteDialog = (props: TransformationReferenceDeleteDialogProps) => {
    const { reference, isDeleting, onClose, onConfirm } = props;
    const { t } = useTranslation();
    const theme = useTheme();

    return (
        <Dialog
            open={reference !== null}
            onClose={isDeleting ? undefined : onClose}
            fullWidth
            maxWidth="xs"
            aria-labelledby="transformation-reference-delete-title"
            PaperProps={{
                sx: {
                    backgroundColor: theme.custom.grey[100],
                    borderRadius: theme.spacing(1.5),
                },
            }}
        >
            <DialogTitle id="transformation-reference-delete-title" sx={{ padding: theme.spacing(3, 4) }}>
                <Typography component="span" variant="h3">
                    {t("common.action.delete", "Delete")}
                </Typography>
            </DialogTitle>
            <DialogContent sx={{ padding: theme.spacing(0, 4, 3) }}>
                {reference !== null && (
                    <Stack gap={theme.spacing(1)}>
                        <Typography variant="body-semibold">{reference.name}</Typography>
                        <Typography variant="body-regular" sx={{ color: theme.custom.grey[2000] }}>
                            {t(
                                "transformation.references.delete_confirm",
                                "Delete this transformation process? This action cannot be undone.",
                            )}
                        </Typography>
                    </Stack>
                )}
            </DialogContent>
            <DialogActions
                sx={{
                    borderTop: `${theme.custom.strokes.containerDivider.width} ${theme.custom.strokes.containerDivider.style} ${theme.custom.strokes.containerDivider.color}`,
                    gap: theme.spacing(2),
                    justifyContent: "space-between",
                    padding: theme.spacing(3, 4),
                }}
            >
                <Button variant="ternary" onClick={onClose} disabled={isDeleting}>
                    {t("primarydialog.cancel", "Cancel")}
                </Button>
                <Button
                    variant="delete"
                    onClick={onConfirm}
                    disabled={isDeleting}
                    aria-label={t("common.action.delete", "Delete")}
                >
                    {isDeleting ?
                        <CircularProgress size={theme.spacing(2.5)} color="inherit" />
                    :   t("common.action.delete", "Delete")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
